import React, { memo, useEffect, useReducer, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { objects, validators } from 'investira.sdk';
import services from '../services';
import withMessage from '../hoc/withMessage';
import withResponseHandling from '../hoc/withResponseHandling';

// Context
const PortfoliosContext = React.createContext();

const initialState = {
  portfolios: [],
  portfolio: '',
  portfolio_id: null,
  carteiras: [],
  isLoading: false
};

// Reducers
const reducers = (state = initialState, action) => {
  if (action.type === 'PORTFOLIO_CHANGED') {
    const { portfolio, portfolio_id, carteiras } = action.payload;

    return {
      ...state,
      portfolio_id: portfolio_id,
      portfolio: portfolio,
      carteiras: carteiras
    };
  }
  if (action.type === 'PORTFOLIO_EDIT_OPENED') {
    const { portfolio, portfolio_id } = action.payload;
    return {
      ...state,
      portfolio: portfolio,
      portfolio_id: portfolio_id
    };
  }
  if (action.type === 'PORTFOLIO_EDIT_CANCELED') {
    return {
      ...state,
      portfolio: null,
      portfolio_id: null
    };
  }
  if (action.type === 'CARTEIRAS_EDIT_CHANGED') {
    return {
      ...state,
      carteiras: action.payload
    };
  }
};

// Dispatchs
const acUpdatePortfolio = pPortfolio => ({
  type: 'PORTFOLIO_CHANGED',
  payload: pPortfolio
});

const acEditPortfolioOpened = pObjPortfolio => ({
  type: 'PORTFOLIO_EDIT_OPENED',
  payload: pObjPortfolio
});

const acEditPortfolioCanceled = () => ({
  type: 'PORTFOLIO_EDIT_CANCELED'
});

const acUpdateCarteiras = () => ({
  type: 'CARTEIRAS_EDIT_CHANGED'
});

// Custom Hook
export const usePortfoliosContext = () => React.useContext(PortfoliosContext);

export const PortfoliosProvider = memo(props => {
  const location = useLocation();
  const navigate = useNavigate();
  const storePortfolioCurrent = useSelector(store => store.portfolios.current);
  const storeDispatch = useDispatch();

  const [state, dispatch] = useReducer(reducers, initialState);

  const handleModifyPortfolio = () => {
    dispatch(acEditPortfolioOpened());
  };

  const handleModifyPortfolioCancel = () => {
    dispatch(acEditPortfolioCanceled());
  };

  const handleUpdatePortfolio = pObjPortfolio => {
    dispatch(acUpdatePortfolio(pObjPortfolio));
  };

  const handleEditPortfolio = pPortfolio => {
    console.log('pPortfolio', pPortfolio);
    handleUpdatePortfolio(pPortfolio);
    navigate(`/portfolios/${pPortfolio.portfolio_id}`);
  };

  // const handleUpdateCarteiras = pObjPortfolio => {
  //   dispatch(acUpdateCarteiras(pObjPortfolio));
  // };

  function readPortfolios(pParams, pResolve, pReject, pFinally) {
    services.portfolios.list(
      { ...pParams, sort: 'portfolio ASC' },
      rRes => {
        pResolve && pResolve(rRes);
      },
      rErr => {
        props.onMessageError(rErr.message);
        pReject && pReject(rErr);
      },
      () => {
        pFinally && pFinally();
      }
    );
  }

  function readPortfolio(pPortfolioId, pResolve, pReject, pFinally) {
    services.portfolios.read(
      pPortfolioId,
      rRes => {
        handleUpdatePortfolio(rRes.data[0]);
        pResolve && pResolve();
      },
      rErr => {
        props.responseErrorHandling(rErr);
        pReject && pReject();
      },
      () => {
        pFinally && pFinally();
      }
    );
  }

  function createPortfolio(pData = {}, pResolve, pReject, pFinally) {
    services.portfolios.create(
      pData,
      rRes => {
        props.onMessageSuccess('Portfolio criado.');
        pResolve && pResolve(rRes);
      },
      rErr => {
        props.onMessageError(rErr.message);
        pReject && pReject();
      },
      () => {
        pFinally && pFinally();
      }
    );
  }

  function modifyPortfolio(pData, pResolve, pReject, pFinally) {
    services.portfolios.modify(
      pData,
      rRes => {
        handleUpdatePortfolio(rRes.data);
        pResolve && pResolve(rRes);
      },
      rErr => {
        pReject && pReject(rErr);
      },
      pFinally
    );
  }

  function addCarteira(pAtivo = {}, pResolve, pReject, pFinally) {
    services.portfolios.add(
      pAtivo,
      rRes => {
        return pResolve && pResolve(rRes);
      },
      rErr => {
        pReject && pReject(rErr);
      },
      () => {
        pFinally && pFinally();
      }
    );
  }

  function deleteCarteira(pPortfolioId, pResolve, pReject, pFinally) {
    services.portfolios.remove(
      pPortfolioId,
      rRes => {
        pResolve && pResolve(rRes);
      },
      rErr => {
        pReject && pReject(rErr);
      },
      () => {
        pFinally && pFinally();
      }
    );
  }

  console.log('state', state);

  return (
    <PortfoliosContext.Provider
      value={{
        state: {
          ...state
        },
        actions: {
          handleUpdatePortfolio,
          handleModifyPortfolio,
          handleModifyPortfolioCancel,
          handleEditPortfolio,
          readPortfolios,
          readPortfolio,
          createPortfolio,
          modifyPortfolio,
          addCarteira,
          deleteCarteira
        }
      }}>
      {props.children}
    </PortfoliosContext.Provider>
  );
});

PortfoliosProvider.displayName = 'PortfoliosProvider';

export default withMessage(withResponseHandling(PortfoliosProvider));
